import { create } from 'zustand';
import { endpoints, type MicrosoftAccount, type ServerConfig, type User } from '../services/api';
import { flushSettings } from '../lib/syncFlush';
import { switchProfile } from './switchProfile';

export interface ProviderStatus {
  connected: boolean;
  email: string | null;
  /** Microsoft To Do permission granted (accounts connected earlier must reconnect). */
  tasks?: boolean;
  /** Work/school account (SharePoint, shared calendars, Planner available). */
  work?: boolean;
  /** Extra work access (SharePoint, shared files, shared calendars) granted. */
  workAccess?: boolean;
  /** Colleagues' names can be read (Planner assignees). */
  people?: boolean;
  /** Every connected account (the fields above summarise them). */
  accounts?: MicrosoftAccount[];
}

interface SessionStore {
  config: ServerConfig | null;
  user: User | null;
  loaded: boolean;
  microsoft: ProviderStatus | null;
  /** A Microsoft account couldn't be connected: it belongs to another KCL Canvas account. */
  linkProblem: { code: 'linked_elsewhere' | 'only_sign_in'; email: string } | null;
  setLinkProblem: (p: SessionStore['linkProblem']) => void;
  load: () => Promise<void>;
  setUser: (u: User | null) => void;
  refreshMicrosoft: () => Promise<void>;
  signOut: () => Promise<void>;
}

const offlineConfig: ServerConfig = {
  env: 'offline',
  features: { accounts: false, billing: false, microsoft: false },
  licensePrice: { amount: 1000, currency: 'GBP' },
};

// The last account this screen was signed in to, so a reload while the server is briefly
// unreachable (a deploy, a network blip) carries on as that account instead of showing a
// signed-out screen with no photos.
const LAST_USER_KEY = 'kcl-canvas-last-user';

function readLastUser(): User | null {
  try {
    const raw = localStorage.getItem(LAST_USER_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

function writeLastUser(user: User | null): void {
  try {
    if (user) localStorage.setItem(LAST_USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(LAST_USER_KEY);
  } catch {
    /* storage blocked */
  }
}

/** Retries of an unconfirmed session: 15 s, 30 s, 1, 2, then every 5 minutes. */
let retryTimer: ReturnType<typeof setTimeout> | undefined;
let attempts = 0;
const RETRY_MS = [15_000, 30_000, 60_000, 120_000, 300_000];
/** A server that keeps saying accounts are off after this many tries really has them off. */
const ACCEPT_ACCOUNTS_OFF_AFTER = 5;

export const useSession = create<SessionStore>()((set, get) => ({
  config: null,
  user: null,
  loaded: false,
  microsoft: null,
  linkProblem: null,
  setLinkProblem: (linkProblem) => set({ linkProblem }),
  load: async () => {
    clearTimeout(retryTimer);
    const [config, me] = await Promise.allSettled([endpoints.config(), endpoints.me()]);
    const last = get().user ?? readLastUser();
    const reachable = config.status === 'fulfilled' && me.status === 'fulfilled';
    // A server starting up without its secrets briefly reports accounts as off (so "nobody
    // signed in"). A screen that was signed in doesn't believe that straight away.
    const accountsOff = config.status === 'fulfilled' && !config.value.features.accounts;
    const doubtful = !reachable || (accountsOff && last !== null && attempts < ACCEPT_ACCOUNTS_OFF_AFTER);
    if (doubtful) {
      // Carry on as the last account (its local settings and lists stay on screen) and ask again soon.
      set({
        config: get().config ?? (config.status === 'fulfilled' && !accountsOff ? config.value : offlineConfig),
        user: last,
        loaded: true,
      });
      retryTimer = setTimeout(() => void get().load(), RETRY_MS[Math.min(attempts++, RETRY_MS.length - 1)]);
      if (last && !get().microsoft) await get().refreshMicrosoft();
      return;
    }
    attempts = 0;
    const user = (me as PromiseFulfilledResult<{ user: User | null }>).value.user;
    switchProfile(user?.id);
    writeLastUser(user);
    set({ config: (config as PromiseFulfilledResult<ServerConfig>).value, user, loaded: true });
    await get().refreshMicrosoft();
  },
  setUser: (user) => {
    // Swap to this account's local data before anything (e.g. settings sync) sees the user.
    switchProfile(user?.id);
    writeLastUser(user);
    set({ user });
    void get().refreshMicrosoft();
  },
  refreshMicrosoft: async () => {
    const { user, config } = get();
    if (!user || !config?.features.microsoft) {
      set({ microsoft: null });
      return;
    }
    try {
      set({ microsoft: await endpoints.microsoftStatus() });
    } catch {
      // Unreachable for now: keep what we knew, so OneDrive photos and calendars carry on.
    }
  },
  signOut: async () => {
    await endpoints.logout().catch(() => undefined);
    writeLastUser(null);
    switchProfile(null);
    set({ user: null, microsoft: null });
  },
}));

export const isLicensed = (u: User | null): boolean => u?.license === 'lifetime';

/** Agree to Power BI for a connected Microsoft account (`account`, else the first) – full-page redirect. */
export async function startPowerBi(account?: string): Promise<void> {
  const q = new URLSearchParams({ mode: 'link', access: 'powerbi' });
  if (account) q.set('account', account);
  await flushSettings(); // e.g. "Show Power BI" switched on a moment ago
  window.location.assign(`/api/auth/oauth/microsoft/start?${q}`);
}

/**
 * Starts the Microsoft OAuth flow (full-page redirect). `returnTo` is an app path, e.g. /pair.
 * `link` connects another Microsoft account (Microsoft shows its account chooser). `work`
 * (link only) asks a work/school account – `account`, else the first – for SharePoint,
 * shared files and shared calendars.
 */
export async function startMicrosoft(
  mode: 'signin' | 'link',
  returnTo?: string,
  work = false,
  account?: string,
  /** Take the account over from another KCL Canvas account (pre-selecting `hint`). */
  move?: { hint: string },
): Promise<void> {
  const q = new URLSearchParams({ mode });
  if (returnTo) q.set('returnTo', returnTo);
  if (work && mode === 'link') q.set('access', 'work');
  if (work && account) q.set('account', account);
  if (move && mode === 'link') {
    q.set('move', '1');
    if (move.hint) q.set('hint', move.hint);
  }
  await flushSettings(); // settings changed just before leaving the page
  window.location.assign(`/api/auth/oauth/microsoft/start?${q}`);
}
